import React from 'react';
import SEO from '../components/SEO';

const PrivacyPolicy = () => {
  return (
    <>
      <SEO 
        title="Privacy Policy | Paracca Financial Group"
        description="Privacy Policy for Paracca Financial Group - Learn how we protect and handle your personal information."
      />
      
      <div className="bg-white">
        {/* Hero Section */}
        <section className="bg-[#0a69b2] text-white py-20">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h1 className="font-bellefair text-4xl sm:text-5xl uppercase mb-4">Privacy Policy</h1>
            <p className="font-montserrat text-base text-gray-100">
              Last Updated: {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
            </p>
          </div>
        </section>

        {/* Content Section */}
        <section className="py-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="prose prose-lg max-w-none font-montserrat">
              
              <div className="mb-8">
                <h2 className="font-bellefair text-2xl uppercase mb-4 text-[#0a69b2]">Our Commitment to Your Privacy</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Paracca Financial Group is committed to protecting the privacy and security of your personal information. As a registered investment advisory firm, we understand the importance of maintaining the confidentiality of your financial information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website or use our services.
                </p>
              </div>

              <div className="mb-8">
                <h2 className="font-bellefair text-2xl uppercase mb-4 text-[#0a69b2]">Information We Collect</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  We collect information that you provide directly to us, including:
                </p>
                <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
                  <li>Personal identification information (name, email address, phone number, mailing address)</li>
                  <li>Financial information (income, assets, investment objectives, risk tolerance)</li>
                  <li>Social Security Number or Tax Identification Number</li>
                  <li>Employment and professional information</li>
                  <li>Account credentials and transaction history</li>
                  <li>Communication preferences</li>
                </ul>
                <p className="text-gray-700 leading-relaxed mb-4">
                  We may also collect information automatically through your use of our website, including IP address, browser type, device information, and usage patterns.
                </p>
              </div>

              <div className="mb-8">
                <h2 className="font-bellefair text-2xl uppercase mb-4 text-[#0a69b2]">How We Use Your Information</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  We use the information we collect to:
                </p>
                <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
                  <li>Provide, maintain, and improve our investment advisory services</li>
                  <li>Develop and implement investment strategies tailored to your goals</li>
                  <li>Process transactions and maintain your accounts</li>
                  <li>Communicate with you about your accounts and our services</li>
                  <li>Comply with legal and regulatory requirements</li>
                  <li>Prevent fraud and enhance security</li>
                  <li>Send you marketing communications (with your consent)</li>
                  <li>Analyze and improve our website and services</li>
                </ul>
              </div>

              <div className="mb-8">
                <h2 className="font-bellefair text-2xl uppercase mb-4 text-[#0a69b2]">Information Sharing and Disclosure</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  We do not sell, trade, or rent your personal information to third parties. We may share your information only in the following circumstances:
                </p>
                <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
                  <li><strong>Service Providers:</strong> With third-party service providers who perform services on our behalf (custodians, broker-dealers, technology providers) under strict confidentiality agreements</li>
                  <li><strong>Legal Requirements:</strong> When required by law, regulation, legal process, or government request</li>
                  <li><strong>Professional Advisors:</strong> With your accountant, attorney, or other professional advisors with your consent</li>
                  <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets</li>
                  <li><strong>Protection of Rights:</strong> To protect our rights, property, or safety, or that of our clients or others</li>
                </ul>
              </div>

              <div className="mb-8">
                <h2 className="font-bellefair text-2xl uppercase mb-4 text-[#0a69b2]">Regulation S-P Compliance</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  As a registered investment adviser, we comply with the SEC's Regulation S-P regarding the privacy of consumer financial information. We maintain physical, electronic, and procedural safeguards designed to protect your nonpublic personal information.
                </p>
              </div>

              <div className="mb-8">
                <h2 className="font-bellefair text-2xl uppercase mb-4 text-[#0a69b2]">Data Security</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  We implement appropriate technical and organizational measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. These measures include:
                </p>
                <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
                  <li>Encryption of sensitive data in transit and at rest</li>
                  <li>Secure access controls and authentication procedures</li>
                  <li>Regular security assessments and updates</li>
                  <li>Employee training on data protection and confidentiality</li>
                  <li>Limited access to personal information on a need-to-know basis</li>
                </ul>
                <p className="text-gray-700 leading-relaxed mb-4">
                  However, no method of transmission over the internet or electronic storage is 100% secure. While we strive to protect your information, we cannot guarantee absolute security.
                </p>
              </div>

              <div className="mb-8">
                <h2 className="font-bellefair text-2xl uppercase mb-4 text-[#0a69b2]">Your Rights and Choices</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  You have certain rights regarding your personal information:
                </p>
                <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
                  <li><strong>Access:</strong> You may request access to the personal information we hold about you</li>
                  <li><strong>Correction:</strong> You may request correction of inaccurate or incomplete information</li>
                  <li><strong>Opt-Out:</strong> You may opt out of receiving marketing communications from us</li>
                  <li><strong>Deletion:</strong> You may request deletion of your information, subject to legal and regulatory requirements</li>
                </ul>
                <p className="text-gray-700 leading-relaxed mb-4">
                  To exercise these rights, please contact us using the information provided below.
                </p>
              </div>

              <div className="mb-8">
                <h2 className="font-bellefair text-2xl uppercase mb-4 text-[#0a69b2]">Cookies and Tracking Technologies</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Our website may use cookies and similar tracking technologies to enhance your experience, analyze usage patterns, and deliver personalized content. You can control cookies through your browser settings, though disabling cookies may limit some functionality of our website.
                </p>
              </div>

              <div className="mb-8">
                <h2 className="font-bellefair text-2xl uppercase mb-4 text-[#0a69b2]">Third-Party Links</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Our website may contain links to third-party websites. We are not responsible for the privacy practices or content of these external sites. We encourage you to review the privacy policies of any third-party sites you visit.
                </p>
              </div>

              <div className="mb-8">
                <h2 className="font-bellefair text-2xl uppercase mb-4 text-[#0a69b2]">Children's Privacy</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Our services are not directed to individuals under the age of 18. We do not knowingly collect personal information from children. If we become aware that we have collected information from a child, we will take steps to delete such information.
                </p>
              </div>

              <div className="mb-8">
                <h2 className="font-bellefair text-2xl uppercase mb-4 text-[#0a69b2]">Changes to This Privacy Policy</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  We may update this Privacy Policy from time to time to reflect changes in our practices or legal requirements. We will notify you of any material changes by posting the updated policy on our website with a revised "Last Updated" date. Your continued use of our services after such changes constitutes acceptance of the updated policy.
                </p>
              </div>

              <div className="mb-8">
                <h2 className="font-bellefair text-2xl uppercase mb-4 text-[#0a69b2]">Contact Us</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  If you have questions or concerns about this Privacy Policy or our privacy practices, please contact us:
                </p>
                <div className="bg-gray-50 p-6 rounded-lg">
                  <p className="text-gray-700 mb-2"><strong>Paracca Financial Group</strong></p>
                  <p className="text-gray-700 mb-2">428 S Tamiami Trail, Osprey, FL 34229</p>
                  <p className="text-gray-700 mb-2">Phone: <a href="tel:941-545-9057" className="text-[#0a69b2] hover:underline">941-545-9057</a></p>
                  <p className="text-gray-700">Email: <a href="mailto:contact@paracca.com" className="text-[#0a69b2] hover:underline">contact@paracca.com</a></p>
                </div>
              </div>

            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default PrivacyPolicy;
